import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import styled, { useTheme } from 'styled-components';
import { ReactComponent as LeftCaret } from '../Icons/LeftCaret.svg';
import { ReactComponent as Ellipse } from '../../../assets/images/Ellipse.svg';
import { ReactComponent as ExportPdf } from '../../../assets/images/PDFIcon.svg';
import { ReactComponent as CornIcon } from '../FieldPlan/Icons/CornIcon.svg';
import { ReactComponent as SoyIcon } from '../FieldPlan/Icons/SoybeanIcon.svg';
import { AppDispatch, RootState, useAppDispatch } from '../../../logic/store/Store';
import { connect, ConnectedProps } from 'react-redux';
import { getCurrentActingUser } from '../../../logic/store/User/AuthSlice';
import { Button } from '../../../components/Button/Button';
import { Tooltip, Radio, RadioChangeEvent, Divider } from 'antd';
import { IconButton } from '../../../components/IconButton/IconButton';
import { themeGet } from '@styled-system/theme-get';
import { cornId, soyId } from '../../../logic/store/Seeds/CropsSlice';
import { useDiscountEditing } from './useDiscountEditing';
import { CropLevelDiscountMatrix } from './CropLevelDiscountMatrix';
import { PlanDiscountSummaryRow } from './PlanDiscountSummaryRow';
import { startPlanPdf } from '../../../logic/Utility/registerCancelableThunks';

const HeaderContainer = styled.div`
	display: flex;
	flex: 1 1 auto;
	padding-right: 2%;
	padding-left: 2%;
	flex-direction: column;
`;

const HeaderButton = styled(Button)`
	font-size: 14px;
	padding: 0 16px 0 16px;
	height: 32px;
	border-width: 2px;
`;

const HeaderRadio = styled(Radio)`
	margin-left: 35px;
	margin-right: 2px;
	.ant-radio-inner {
		border-color: ${themeGet('colors.primary')};
	}
	.ant-radio-checked .ant-radio-inner {
		border-color: ${themeGet('colors.primary')};
	}
	.ant-radio-checked::after {
		border-color: ${themeGet('colors.primary')};
	}
	.ant-radio-checked .ant-radio-inner::after {
		background-color: ${themeGet('colors.primary')};
	}
	.ant-radio-wrapper:hover .ant-radio, .ant-radio:hover .ant-radio-inner, .ant-radio-input:focus + .ant-radio-inner {
		border-color: ${themeGet('colors.primary')};
	}
	.ant-radio-input:focus + .ant-radio-inner {
		box-shadow: none;
	}
	span.ant-radio + * {
		font-weight: ${themeGet('fontWeights.bold')};
		padding-right: 0;
	}
	.ant-radio-wrapper {
		margin-right: 0;
	}
`;

const StyledDivider = styled(Divider)`
	&.ant-divider {
		border-top-color: black;
	}
`;


const StyledCornIcon = styled(CornIcon)<{ fillcolor: string }>`
	path {
		fill: ${props => props.fillcolor};
	}
`;

const StyledSoyIcon = styled(SoyIcon)<{ fillcolor: string }>`
	path {
		fill: ${props => props.fillcolor};
	}
`;

export function StyledCropIcon(props: { cropKey: CropKey, fillcolor: string }) 
{
	const { cropKey, fillcolor } = props;

	switch (cropKey) 
	{
		case 'Corn':
			return <StyledCornIcon fillcolor={fillcolor} />;
		case 'Soybean':
			return <StyledSoyIcon fillcolor={fillcolor} />;
		default:
			return null;
	}
}

export type CropKey = 'Corn' | 'Soybean';
export type CropDisplay = 'Both' | 'Corn' | 'Soybean';

export const cropIds: Record<CropKey, string> = {
	Corn: cornId,
	Soybean: soyId,
};

export const cropKeys: Record<string, CropKey> = {
	[cornId]: 'Corn',
	[soyId]: 'Soybean',
};

const mapStateToProps = (state: RootState) => 
{
	const SelectedGrower = state.grower.Growers.find(g => g.Id === state.ui.SelectedGrowerId);
	const SelectedPlan = SelectedGrower?.Plans?.find(p => p.Id === state.ui.SelectedPlanId);
	return {
		SelectedGrower,
		SelectedPlan,
		CurrentActingUser: getCurrentActingUser(state),
		DiscountReasonCodes: state.userInfo.DiscountReasonCodes,
		IsLoadingCA: state.grower.isLoading,
	};
};

const mapDispatchToProps = (dispatch: AppDispatch) =>
{
	return {};
};

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

export interface ICompetitiveAllowanceProps extends PropsFromRedux
{
}

const CompetitiveAllowanceComponent = (props: ICompetitiveAllowanceProps) =>
{
	const {
		SelectedGrower,
		SelectedPlan,
		CurrentActingUser,
		IsLoadingCA,
	} = props;

	const [selectedCropDisplay, setSelectedCropDisplay] = useState<CropDisplay>('Both');
	
	const {
		discountState,
		addCropLevelRow,
		updateCropLevelRow,
		deleteCropLevelRow,
		updateHybridLevelRow,
		save,
	} = useDiscountEditing();

	const theme = useTheme();
	const dispatch = useAppDispatch();
	
	const isReadOnly =  SelectedPlan?.Status === 'Accepted';
	const cornExists = discountState.discounts?.Corn.hybrids.length > 0;
	const soyExists = discountState.discounts?.Soybean.hybrids.length > 0;

	const downloadPdf = () =>
	{
		dispatch(startPlanPdf({ PlanId: SelectedPlan.Id, CoverConfig: undefined }));
	};

	const selectCropDisplay = (e: RadioChangeEvent) =>
	{
		setSelectedCropDisplay(e.target.value);
	};


	const isSaveDisabled = useMemo(() =>
	{
		if(!discountState || !SelectedPlan?.CompetitiveAllowance)
		{
			return true;
		}

		if (isReadOnly)
		{
			return true;
		}

		if (cornExists && (discountState.discounts.Corn.crop.some(d => !d.DiscountReasonCode || d.PerBagDiscount === 0)))
		{
			return true;
		}

		if (soyExists && (discountState.discounts.Soybean.crop.some(d => !d.DiscountReasonCode || d.PerBagDiscount === 0)))
		{
			return true;
		}

		if (SelectedPlan.CompetitiveAllowance?.PlanHybridDiscounts.some(d =>
			(d.DiscountReasonCode && d.PerBagDiscount === 0) || (!d.DiscountReasonCode && d.PerBagDiscount > 0)))
		{
			return true;
		}

		return false;
	}, [discountState, isReadOnly, cornExists, soyExists, SelectedPlan?.CompetitiveAllowance]);

	return (
		<div style={{ width: '100%', height: '100%', paddingLeft: 70, backgroundColor: theme.colors.backgroundLM, display: 'flex', flexDirection: 'column' }}>
			<div className='CompetitiveAllowance_Header'>
				<div
					className='backTo_GrowerList_Container'
					style={{
						display: 'flex',
						height: 52,
						padding: '10px 25px',
						alignItems: 'center',
					}}
				>
					<Link to='/fieldactivities/recall' className='linkTo_PlanList'>
						<LeftCaret style={{ marginRight: '20px' }} />
						<span style={{ color: theme.colors.darkGrey }}>{SelectedGrower?.Name}</span>
					</Link>
					<Ellipse style={{ margin: '0 10px' }} />
					<span style={{ fontWeight: theme.fontWeights.bold }}>Proposal / Discount Review</span>
				</div>
				<HeaderContainer>
					<div style={{ display: 'flex', alignItems: 'start', height: 'auto' }}>
						<div
							className='CompetitiveAllowance_Title'
							style={{ fontWeight: theme.fontWeights.bold, fontSize: theme.fontSizes.large, fontFamily: theme.fonts.heading }}
						>
							{`Grower: ${SelectedGrower?.Name} | Associated Seller: ${CurrentActingUser.UserName}`}
						</div>
						<div style={{ marginLeft: 'auto', display: 'flex' }}>
							<IconButton
								tooltip={''}
								placement='bottom'
								onClick={() => downloadPdf()}
								fillcolor='auto' // purposefully incorrect so as to not have a fillcolor
								style={{height: 32, width: 32, marginRight: 1}}
								disabled={false}
								showDisabledTooltip= { true }
							>
								<ExportPdf height={40} width={40} style={{ position: 'relative' }} />
							</IconButton>
							<Tooltip title={''} placement='bottom'>
								<span>
									<HeaderButton style={{ marginLeft: 16 }} variant='dark' disabled={isSaveDisabled} onClick={save}>
										Save Proposal
									</HeaderButton>
								</span>
							</Tooltip>
						</div>
					</div>
					<div style={{ display: 'flex', alignItems: 'start', height: 'auto', marginTop: 25 }}>
						<div className='CompetitiveAllowance_SecondaryTitle' style={{ fontSize: theme.fontSizes.normal, fontWeight: theme.fontWeights.bold }}>
							{'Detailed Pricing and Units'}
						</div>
						<div style={{ marginLeft: 'auto', display: 'flex' }}>
							<Radio.Group onChange={selectCropDisplay} value={selectedCropDisplay}>
								<HeaderRadio value={'Both'}>Both</HeaderRadio>
								<HeaderRadio
									value={'Corn'}
									disabled={!cornExists}
								>
									Corn
								</HeaderRadio>
								<HeaderRadio
									value={'Soybean'}
									disabled={!soyExists}
								>
									Soybean
								</HeaderRadio>
							</Radio.Group>
						</div>
					</div>
					<PlanDiscountSummaryRow
						planName={SelectedPlan?.Name}
						discountState={discountState}
						cornExists={cornExists}
						soyExists={soyExists}
					/>
				</HeaderContainer>
			</div>
			<div style={{ paddingRight: '2%', paddingLeft: '2%', marginTop: 10 }}>
				<StyledDivider className='Divider' />
			</div>
			{
				!IsLoadingCA && !discountState.discounts ?
					<div
						style={{
							paddingRight: '2%',
							paddingLeft: '2%',
							display: 'flex',
							flexDirection: 'column',
							flex: '1 1 auto',
							overflowY: 'auto',
							fontSize: theme.fontSizes.large,
							fontWeight: theme.fontWeights.bold,
							textAlign: 'center'
						}}>
						No Available Hybrids to Apply Discounts on this Plan.
					</div>
					:
					<div style={{ paddingRight: '2%', paddingLeft: '2%', display: 'flex', flexDirection: 'column', flex: '1 1 auto', overflowY: 'auto' }}>
						{
							(['Corn', 'Soybean'] as CropKey[]).map((cropKey) => <CropLevelDiscountMatrix cropKey={cropKey}
								selectedCropDisplay={selectedCropDisplay}
								isReadOnly={isReadOnly}
								discountState={discountState}
								updateCropLevelRow={updateCropLevelRow}
								deleteCropLevelRow={deleteCropLevelRow}
								addCropLevelRow={addCropLevelRow}
								updateHybridLevelRow={updateHybridLevelRow}
								key={cropKey}	
							/>)
						}
					</div>
			}
		</div>
	);
};

export const CompetitiveAllowance = connector(CompetitiveAllowanceComponent);


